import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { authService } from "../../services/auth.service";

const ResetPassword = () => {
  const { t } = useTranslation("auth");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";
  const email = searchParams.get("email") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      setError(t("resetPassword.invalidLink"));
    }
  }, [token, email, t]);

  useEffect(() => {
    if (!redirecting) return;
    const id = setTimeout(() => navigate("/login"), 3000);
    return () => clearTimeout(id);
  }, [redirecting, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError(t("resetPassword.passwordTooShort"));
      return;
    }
    if (newPassword !== confirm) {
      setError(t("resetPassword.mismatch"));
      return;
    }
    setIsLoading(true);
    try {
      await authService.resetPassword(token, email, newPassword);
      setSuccess(true);
      setRedirecting(true);
    } catch {
      setError(t("resetPassword.invalidToken"));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    "appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";

  return (
    <Layout showSidebar={false}>
      <div className="flex min-h-[80vh] items-center justify-center">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 space-y-6">
          {success ? (
            <div className="text-center space-y-4">
              <div className="text-5xl">✅</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t("resetPassword.successTitle")}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {t("resetPassword.successHint")}
              </p>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {t("resetPassword.title")}
                </h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">
                    {error}
                    {(error === t("resetPassword.invalidToken") ||
                      error === t("resetPassword.invalidLink")) && (
                      <div className="mt-2">
                        <Link to="/forgot-password" className="underline">
                          {t("resetPassword.requestNewLink")}
                        </Link>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("resetPassword.newPassword")}
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className={inputClass}
                    placeholder={t("resetPassword.passwordPlaceholder")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("resetPassword.confirmPassword")}
                  </label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className={inputClass}
                    placeholder={t("resetPassword.passwordPlaceholder")}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading || !token || !email}
                  className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? t("resetPassword.saving") : t("resetPassword.submit")}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ResetPassword;
